import { useEffect, useState } from "react";
import { getAllCustomers } from "../services/customerService";
import { fetchBrands } from "../services/brandService";
import { needsBrandDetails } from "../features/onboarding-agent/brandDetailsFromProfile";

export type BusinessProfilesStatus =
  | "idle" // disabled (auth loading, admin, or gate bypassed) — never fetches
  | "checking"
  | "has-profiles"
  | "no-profiles"
  | "needs-details"; // every Brand is still at onboarding step 1 (SGL-512)

/**
 * Whether the current user has any business profiles.
 *
 * Used by OnboardingGate to route a first-time Customer (zero profiles) to the
 * onboarding assistant instead of the dead-end empty agent board. We check the
 * real profile list rather than trusting user.profile_created, which isn't
 * reliably set to false for new trial signups.
 *
 * - idle:          `enabled` is false (auth loading, admin user, or bypassed) — no fetch
 * - checking:      fetching the profile list
 * - has-profiles:  at least one business profile exists
 * - no-profiles:   the customer has zero business profiles
 * - needs-details: every profile is ONBOARDING with no Brand details yet (the
 *                  owner left setup after step 1); `resumeBrandId` is the newest
 *
 * Fails open: on a fetch error it reports "has-profiles" so a failed check
 * never traps a user out of the app.
 *
 * @param idToken  auth token (null while auth is loading)
 * @param enabled  gate the fetch — pass false for admins and while auth loads
 */
export function useHasBusinessProfiles(
  idToken: string | null,
  enabled: boolean
): { status: BusinessProfilesStatus; resumeBrandId: string | null } {
  const [status, setStatus] = useState<BusinessProfilesStatus>("idle");
  const [resumeBrandId, setResumeBrandId] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !idToken) {
      setStatus("idle");
      return;
    }
    let cancelled = false;
    setStatus("checking");
    (async () => {
      const list = await getAllCustomers(idToken);
      if (!Array.isArray(list) || list.length === 0) return { status: "no-profiles" as const, id: null };
      // Only when no Brand is past step 1: an owner with a working Brand is
      // never pulled back into setup by a second, unfinished one.
      if (!list.every((c) => c.status === "ONBOARDING")) return { status: "has-profiles" as const, id: null };
      const newest = [...list].sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))[0];
      const { brands } = await fetchBrands(newest.customer_id, idToken);
      return needsBrandDetails(newest.status, brands[0])
        ? { status: "needs-details" as const, id: newest.customer_id }
        : { status: "has-profiles" as const, id: null };
    })()
      .then((r) => {
        if (cancelled) return;
        setResumeBrandId(r.id);
        setStatus(r.status);
      })
      .catch(() => {
        // Fail open — a failed check must never trap the user out of the app.
        if (!cancelled) setStatus("has-profiles");
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, enabled]);

  return { status, resumeBrandId };
}
